/**
 * Registers the Android FCM token with the backend so the user's phone can
 * receive push notifications for alerts.
 *
 * Flow:
 *   1. MainActivity.java reads the FCM token from SharedPreferences and
 *      injects it into the WebView as `window.warnixNative.fcmToken`, then
 *      fires the `warnixNativeReady` DOM event.
 *   2. This hook listens for that event (and also handles the case where
 *      the event already fired before mount), POSTs the token to
 *      `/api/fcm/register`, and remembers the last-registered token in
 *      localStorage so we don't re-register on every render.
 *
 * Web users (no `window.warnixNative`) become a no-op — there is no web
 * push pipeline in v2, only Android.
 */
import { useEffect } from "react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

declare global {
  interface Window {
    warnixNative?: {
      platform?: "android" | "ios";
      fcmToken?: string;
      pendingAlertId?: string;
    };
  }
}

const LAST_TOKEN_KEY = "warnix_v2_fcm_last_token";

async function registerFcmToken(token: string, platform: "android"): Promise<void> {
  const last = (() => {
    try {
      return localStorage.getItem(LAST_TOKEN_KEY);
    } catch {
      return null;
    }
  })();
  if (last === token) return;

  try {
    await api("/api/fcm/register", {
      method: "POST",
      body: { token, platform },
    });
    try {
      localStorage.setItem(LAST_TOKEN_KEY, token);
    } catch {
      // localStorage write failures are non-fatal; we'll just retry on next mount.
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      throw err;
    }
    // Surface in console so the user can debug — but don't crash the app
    // because the dashboard is still usable without push.
    console.warn("[fcm] register failed", err);
  }
}

export function useNativeFcm(): void {
  const token = useAuth((s) => s.token);

  useEffect(() => {
    if (!token) return;

    const tryRegister = () => {
      const native = window.warnixNative;
      if (!native || !native.fcmToken) return;
      const platform = native.platform === "android" ? "android" : "android";
      void registerFcmToken(native.fcmToken, platform);
    };

    // The native side may have already fired the event before this hook
    // mounted (it fires 1.5s after onResume) — try immediately.
    tryRegister();

    window.addEventListener("warnixNativeReady", tryRegister);
    return () => window.removeEventListener("warnixNativeReady", tryRegister);
  }, [token]);
}
